import { onMounted, onBeforeMount, computed } from 'vue';
import { useSiteContentStore } from '~/stores/siteContent';
import { usePageContentStore } from '~/stores/pageContent';
import { useFormMetaDataStore } from '~/stores/formMetaData';
import { useUserContextStore } from '~/stores/userContext';

export function usePageContent() {
  const siteContentStore = useSiteContentStore();
  const pageContentStore = usePageContentStore();
  const formMetaDataStore = useFormMetaDataStore();
  const userContextStore = useUserContextStore();
  const config = useRuntimeConfig();
  const route = useRoute();

  let checkIfScrollToHashTimeout: NodeJS.Timeout;

  const checkIfScrollToHash = () => {
    if (window.location.hash) {
      checkIfScrollToHashTimeout = setTimeout(() => {
        const elmnt = document.getElementById(window.location.hash.substr(1));
        elmnt?.scrollIntoView();
      }, 1000);
    }
  };

  const checkIfRedirect = () => {
    const redURL: string | undefined = metaData.value.redirectUrl;
    if (window && redURL && redURL !== '' && redURL !== undefined) {
      window.location.replace(redURL);
    }
  };

  onBeforeMount(() => {
    if (getFormMetadata.value?.carBrand) {
      const carBrand = getFormMetadata.value?.carBrand;
      formMetaDataStore.changeBrand(carBrand);
      if (['BMW', 'MINI', 'XPENG'].includes(carBrand)) userContextStore.setCurrentCarBrand(carBrand);
      else if (['MERCEDES-BENZ'].includes(carBrand.toUpperCase())) {
        const brand = carBrand.split('-');
        userContextStore.setCurrentCarBrand(
          brand[0].charAt(0).toUpperCase() +
            brand[0].slice(1).toLocaleLowerCase() +
            '-' +
            brand[1].charAt(0)?.toUpperCase() +
            brand[1].slice(1).toLocaleLowerCase()
        );
        /**
         * Toyota required that any menu shown do not display the tire or accessory pages as options if a Toyota page or car is selected (MON-7119).
         * This solution was implemented to satisfy this requirement.
         * However, if a customer is browsing for cars, selects a Toyota car and previews it,
         * this rule should not apply.
         */
      } else if (route.path.includes('/bilar/sok-bil/toyota/')) {
        return;
      } else userContextStore.setCurrentCarBrand(carBrand.charAt(0).toUpperCase() + carBrand.slice(1).toLowerCase());
    }
  });

  onBeforeUnmount(() => {
    clearTimeout(checkIfScrollToHashTimeout);
  });

  onMounted(() => {
    checkIfScrollToHash();
    checkIfRedirect();
    if (getFormMetadata?.value?.branch) {
      formMetaDataStore.changeDepartmentId(String(getFormMetadata.value?.branch));
    }
    if (getFormMetadata?.value?.carModel) {
      formMetaDataStore.changeModel(String(getFormMetadata.value?.carModel));
    }
    if (pageContentStore.activeConfigurationGroupId) {
      formMetaDataStore.changeActiveConfigurationGroupId(pageContentStore.activeConfigurationGroupId);
    }
    if (getFormMetadata?.value?.isTransportVehicleFallback) {
      formMetaDataStore.changeIsTransportVehicleFallback(getFormMetadata.value?.isTransportVehicleFallback);
    }
  });

  const languageCode = (): string => {
    return siteContentStore.languageCode;
  };

  const host = (): string => {
    return siteContentStore.host;
  };

  const robotsNoIndex = computed((): string => {
    return host().startsWith('wwwdev') || host().startsWith('wwwtest') ? 'noindex' : metaData.value.noIndex ? 'noindex' : 'all';
  });

  const getFormMetadata = computed(() => {
    return pageContentStore.formMetaData;
  });

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const metaData = computed((): any => {
    return {
      locale: pageContentStore.metadata?.locale,
      noIndex: pageContentStore.metadata?.noIndex,
      name: pageContentStore.metadata?.name,
      redirectUrl: pageContentStore.metadata?.redirectUrl,
      title: pageContentStore.metadata?.title,
      description: pageContentStore.metadata?.description,
      image: pageContentStore.metadata?.image,
      url: pageContentStore.metadata?.url,
      siteName: siteContentStore?.siteName,
    };
  });

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const headScript = computed((): any => {
    const headerScript = siteContentStore.headerScript;

    if (headerScript !== undefined) {
      return {
        innerHTML: headerScript,
        type: 'text/javascript',
      };
    } else {
      return {};
    }
  });

  if (config.public.googleSiteVerification && config.public.googleSiteVerification.length) {
    useHead({
      meta: [{ name: 'google-site-verification', content: config.public.googleSiteVerification }],
    });
  }

  if (config.public.clarityId && config.public.clarityId.length) {
    if (import.meta.client && window) {
      window.addEventListener('CookiebotOnAccept', function () {
        useHead({
          script: [
            {
              type: 'text/javascript',
              defer: true,
              async: true,
              innerHTML: `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${config.public.clarityId}");
          `,
            },
          ],
        });
      });
    }
  }

  const setMetaData = (customUrl: string | null) => {
    const disallowedPageTypes = ['ProductDetailPage', 'CarObjectPage', 'BranchPage'];
    const disallowMeta = disallowedPageTypes.includes(pageContentStore.pageType);

    useSeoMeta({
      title: disallowMeta ? undefined : metaData.value?.title ?? '',
      description: metaData.value?.description ?? '',
      ogTitle: metaData.value?.title ?? '',
      ogDescription: metaData.value?.description ?? '',
      ogUrl: metaData.value?.url ?? '',
      ogImage: metaData.value?.image ?? '',
      twitterTitle: metaData.value?.title ?? '',
      twitterImage: metaData.value?.image ?? '',
      twitterDescription: metaData.value?.description ?? '',
      twitterSite: metaData.value?.siteName ?? '',
      robots: robotsNoIndex.value,
      ogSiteName: metaData.value?.siteName ?? '',
    });

    useHead({
      title: disallowMeta ? undefined : metaData?.value?.title ?? '',
      script: headScript.value,
      htmlAttrs: {
        lang: languageCode,
      },
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: customUrl ? customUrl : metaData.value?.url,
        },
      ],
      meta: [
        {
          hid: 'language',
          name: 'language',
          content: metaData.value?.locale ?? '',
        },
        {
          property: 'facebook-domain-verification',
          content: config.public.facebookDomainVerification,
        },
      ],
    });
  };

  return { setMetaData: setMetaData };
}
